import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ActivityMetricsWidget = ({ device = 'Garmin' }) => {
  const [metrics, setMetrics] = useState({
    steps: 8547,
    stepsGoal: 10000,
    calories: 1842,
    caloriesGoal: 2400,
    distance: 6.2,
    distanceGoal: 8.0,
    activeMinutes: 47,
    activeGoal: 60,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(prev => ({
        ...prev,
        steps: Math.min(prev?.stepsGoal, prev?.steps + Math.floor(Math.random() * 8)),
        calories: Math.min(prev?.caloriesGoal, prev?.calories + Math.floor(Math.random() * 3)),
        distance: Math.min(prev?.distanceGoal, parseFloat((prev?.distance + Math.random() * 0.01)?.toFixed(2))),
      }));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const metricItems = [
    {
      label: 'Steps',
      value: metrics?.steps?.toLocaleString(),
      goal: metrics?.stepsGoal?.toLocaleString(),
      pct: Math.round((metrics?.steps / metrics?.stepsGoal) * 100),
      icon: 'Footprints',
      color: '#22c55e',
      unit: 'steps',
    },
    {
      label: 'Calories',
      value: metrics?.calories?.toLocaleString(),
      goal: metrics?.caloriesGoal?.toLocaleString(),
      pct: Math.round((metrics?.calories / metrics?.caloriesGoal) * 100),
      icon: 'Flame',
      color: '#f97316',
      unit: 'kcal',
    },
    {
      label: 'Distance',
      value: metrics?.distance?.toFixed(1),
      goal: metrics?.distanceGoal?.toFixed(1),
      pct: Math.round((metrics?.distance / metrics?.distanceGoal) * 100),
      icon: 'MapPin',
      color: '#8b5cf6',
      unit: 'km',
    },
    {
      label: 'Active Min',
      value: metrics?.activeMinutes,
      goal: metrics?.activeGoal,
      pct: Math.round((metrics?.activeMinutes / metrics?.activeGoal) * 100),
      icon: 'Timer',
      color: '#06b6d4',
      unit: 'min',
    },
  ];

  return (
    <div className="bg-card rounded-2xl p-5 border border-border/40 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-green-500/10 flex items-center justify-center">
            <Icon name="Activity" size={18} color="#22c55e" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">Activity Metrics</h3>
            <p className="text-xs text-muted-foreground">via {device}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-500/10 text-green-600 text-xs font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          Live
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 flex-1">
        {metricItems?.map((item, idx) => (
          <motion.div
            key={item?.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.08 }}
            className="bg-muted/20 rounded-xl p-3 flex flex-col gap-2"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${item?.color}15` }}>
                  <Icon name={item?.icon} size={14} color={item?.color} />
                </div>
                <span className="text-xs font-medium text-muted-foreground">{item?.label}</span>
              </div>
              <span className="text-xs font-bold" style={{ color: item?.color }}>{item?.pct}%</span>
            </div>

            <div>
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-foreground leading-none">{item?.value}</span>
                <span className="text-xs text-muted-foreground">{item?.unit}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">Goal: {item?.goal}</div>
            </div>

            <div className="h-1.5 bg-muted/40 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: item?.color }}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(item?.pct, 100)}%` }}
                transition={{ duration: 1.2, delay: idx * 0.1, ease: 'easeOut' }}
              />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ActivityMetricsWidget;
